/**
 * ============================================================================
 *  BottomControls — 图板左下角控制栏（缩放 + 快捷键面板）
 * ============================================================================
 *
 * 包含：
 *   1. 缩放控制胶囊（ - | 百分比 | + ）
 *   2. 「?」快捷键帮助按钮 → ShortcutsPopover 弹窗
 *
 * ShortcutsPopover 中的「语音输入」部分（可配置快捷键）：
 *   - 点击白色方框 → 进入 isListening 模式（显示「请按组合键...」）
 *   - 按下任意组合键 → 保存到 localStorage → 广播 VOICE_SHORTCUT_CHANGED
 *   - ✕ 按钮清除快捷键
 *   - 快捷键配置保存后，在 EditorPage 中挂载的 useVoiceShortcut() Hook
 *     会自动读取并生效（全局 keydown/keyup 监听）
 *
 * 快捷键配置数据流：
 *   localStorage('voice_shortcut') ← setVoiceShortcut()
 *   ↓ 广播 VOICE_SHORTCUT_CHANGED
 *   useVoiceShortcut → shortcutRef 同步 → keydown 监听生效
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useReactFlow, useViewport } from 'reactflow';
import { Plus, Minus, HelpCircle, X, MousePointerClick, Hand, Mouse, Mic, Magnet, LayoutGrid, CornerUpLeft, CornerUpRight, Sun, Moon } from 'lucide-react';
import {
  bottomContainerStyle, zoomCapsuleStyle, iconBtnStyle, helpBtnStyle,
  popoverStyle, headerStyle, shortcutRowStyle, keyBadgeStyle
} from '../constants';
import { getVoiceShortcut, setVoiceShortcut, formatShortcut, VOICE_SHORTCUT_CHANGED } from '../hooks/useVoiceShortcut';
import { useSnapEnabled } from '../hooks/useSnapToggle';
import { useTheme } from '../hooks/useTheme';

const ZoomBtn = ({ onClick, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <button 
      onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ ...iconBtnStyle, color: hover ? 'var(--text-hover)' : 'var(--text-secondary)', background: hover ? 'var(--bg-hover)' : 'transparent' }}
    >
      {children}
    </button>
  );
};

const HelpBtn = ({ onClick }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ ...helpBtnStyle, color: hover ? 'var(--text-hover)' : 'var(--text-secondary)', background: hover ? 'var(--btn-hover-solid)' : 'var(--bg-panel)', borderColor: hover ? 'var(--text-hover)' : 'var(--border-subtle)' }}
    >
      <HelpCircle size={20} strokeWidth={1.5} />
    </button>
  );
};

// 磁铁按钮：切换节点对齐吸附（默认开启）。开启时图标高亮为蓝色
const SnapBtn = () => {
  const [enabled, toggle] = useSnapEnabled();
  const [hover, setHover] = useState(false);
  const accent = '#3b82f6';
  const color = enabled ? accent : (hover ? 'var(--text-hover)' : 'var(--text-secondary)');
  return (
    <button
      onClick={toggle} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        ...helpBtnStyle,
        color,
        background: hover ? 'var(--btn-hover-solid)' : 'var(--bg-panel)',
        borderColor: enabled ? 'rgba(59,130,246,0.5)' : (hover ? 'var(--text-hover)' : 'var(--border-subtle)'),
      }}
      title={enabled ? '对齐吸附：开（点击关闭）' : '对齐吸附：关（点击开启）'}
    >
      <Magnet size={18} strokeWidth={1.5} />
    </button>
  );
};

// 主题按钮：切换深色/浅色模式。深色显 Moon、浅色显 Sun，与 HelpBtn/SnapBtn 同款圆形样式
const ThemeBtn = () => {
  const [theme, toggle] = useTheme();
  const [hover, setHover] = useState(false);
  const isLight = theme === 'light';
  return (
    <button
      onClick={toggle} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        ...helpBtnStyle,
        color: hover ? 'var(--text-hover)' : 'var(--text-secondary)',
        background: hover ? 'var(--btn-hover-solid)' : 'var(--bg-panel)',
        borderColor: hover ? 'var(--text-hover)' : 'var(--border-subtle)',
      }}
      title={isLight ? '浅色模式（点击切换深色）' : '深色模式（点击切换浅色）'}
    >
      {isLight ? <Sun size={18} strokeWidth={1.5} /> : <Moon size={18} strokeWidth={1.5} />}
    </button>
  );
};

// 通用圆形快捷按钮：与 HelpBtn/SnapBtn 同款样式，悬停高亮。disabled 时降低透明度并禁用点击
const ActionBtn = ({ onClick, title, disabled, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={disabled}
      style={{
        ...helpBtnStyle,
        color: disabled ? 'var(--text-muted)' : (hover ? 'var(--text-hover)' : 'var(--text-secondary)'),
        background: !disabled && hover ? 'var(--btn-hover-solid)' : 'var(--bg-panel)',
        borderColor: !disabled && hover ? 'var(--text-hover)' : 'var(--border-subtle)',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
      title={title}
    >
      {children}
    </button>
  );
};

const ShortcutsPopover = ({ onClose }) => {
  const [voiceShortcutState, setVoiceShortcutState] = useState(getVoiceShortcut);
  const [isListening, setIsListening] = useState(false);

  // 监听外部变化（如其他地方修改了快捷键）
  useEffect(() => {
    const sync = () => setVoiceShortcutState(getVoiceShortcut());
    window.addEventListener(VOICE_SHORTCUT_CHANGED, sync);
    return () => window.removeEventListener(VOICE_SHORTCUT_CHANGED, sync);
  }, []);

  // 进入"录制快捷键"模式后，捕获下一个组合键
  useEffect(() => {
    if (!isListening) return;
    const handleKeyDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // 忽略单独的修饰键
      if (['Control', 'Alt', 'Shift', 'Meta'].includes(e.key)) return;
      const sc = {
        key: e.key,   // 保留原始 key 作为备用
        code: e.code, // 物理键位（如 KeyZ），不受 macOS Option 键字符映射影响
        ctrlKey: e.ctrlKey,
        altKey: e.altKey,
        shiftKey: e.shiftKey,
        metaKey: e.metaKey,
      };
      setVoiceShortcut(sc);        // 写入 localStorage + 广播事件
      setVoiceShortcutState(sc);
      setIsListening(false);
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isListening]);

  // 点击清除快捷键
  const handleClear = useCallback((e) => {
    e.stopPropagation();
    setVoiceShortcut(null);
    setVoiceShortcutState(null);
    setIsListening(false);
  }, []);

  const formatted = formatShortcut(voiceShortcutState);

  return (
  <div style={popoverStyle} onClick={(e) => e.stopPropagation()}>
    <div style={headerStyle}>
      <span>快捷键</span>
      <div onClick={onClose} style={{cursor: 'pointer', color:'var(--text-secondary)', padding: '2px'}} title="关闭">
        <X size={16} strokeWidth={2}/>
      </div>
    </div>
    <div style={shortcutRowStyle}>
      <span>多选节点</span> 
      <div style={{display:'flex', alignItems:'center', gap:'4px'}}><span style={keyBadgeStyle}>Shift</span> <Plus size={10}/> <MousePointerClick size={14} /></div>
    </div>
    <div style={shortcutRowStyle}>
      <span>平移图板</span>
      <div style={{display:'flex', alignItems:'center', gap:'4px'}}><Hand size={14}/></div>
    </div>
    <div style={shortcutRowStyle}>
      <span>缩放图板</span>
      <div style={{display:'flex', alignItems:'center', gap:'4px'}}><span style={keyBadgeStyle}>Ctrl</span> <Plus size={10}/> <Mouse size={14}/></div>
    </div>
    <div style={shortcutRowStyle}>
      <span>复制 / 粘贴</span> 
      <div style={{display:'flex', alignItems:'center', gap:'4px'}}><span style={keyBadgeStyle}>Ctrl</span> <Plus size={10}/> <span style={keyBadgeStyle}>C / V</span></div>
    </div>
    <div style={shortcutRowStyle}>
      <span>撤销</span> 
      <div style={{display:'flex', alignItems:'center', gap:'4px'}}><span style={keyBadgeStyle}>Ctrl</span> <Plus size={10}/> <span style={keyBadgeStyle}>Z</span></div>
    </div>
    <div style={shortcutRowStyle}>
      <span>重做</span> 
      <div style={{display:'flex', alignItems:'center', gap:'4px'}}><span style={keyBadgeStyle}>Ctrl</span> <Plus size={10}/> <span style={keyBadgeStyle}>A</span></div>
    </div>
    <div style={shortcutRowStyle}>
      <span>删除选中</span> 
      <span style={keyBadgeStyle}>Del</span>
    </div>
    <div style={{...shortcutRowStyle, marginBottom: 0}}>
      <span>适应图板</span>
      <span style={keyBadgeStyle}>Z</span>
    </div>

    {/* ── 语音输入快捷键（可配置） ── */}
    <div style={{ height: '1px', background: 'var(--border-subtle)', margin: '10px 0' }} />
    <div style={{...shortcutRowStyle, marginBottom: 4}}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Mic size={12} /> 语音输入</span>
      <span style={{fontSize:'10px', color:'var(--text-muted)'}}>按住触发</span>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      {/* 快捷键显示 / 录制区域 */}
      <div
        onClick={() => setIsListening(true)}
        style={{
          flex: 1,
          height: '28px',
          borderRadius: '6px',
          border: isListening
            ? '1px solid rgba(99,102,241,0.8)'
            : '1px solid var(--border-strong)',
          background: isListening
            ? 'rgba(99,102,241,0.15)'
            : 'var(--bg-hover)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '11px',
          color: isListening ? '#a5b4fc' : (formatted ? 'var(--text-body)' : 'var(--text-muted)'),
          fontFamily: 'monospace',
          transition: 'all 0.15s',
          gap: '4px',
        }}
        title="点击后按下组合键进行设置"
      >
        {isListening
          ? '请按组合键...'
          : formatted
            ? formatted
            : '点击设置快捷键'}
      </div>
      {/* 清除按钮 */}
      {voiceShortcutState && !isListening && (
        <div
          onClick={handleClear}
          style={{
            width: '28px', height: '28px', borderRadius: '6px',
            border: '1px solid var(--border-strong)',
            background: 'var(--bg-hover)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '14px',
            transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.4)'; e.currentTarget.style.color = '#ef4444'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
          title="清除快捷键"
        >
          <X size={12} />
        </div>
      )}
    </div>
    <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '6px', lineHeight: '1.4' }}>
      聚焦文本框时按住快捷键开始录音，松开停止
    </div>
  </div>
  );
};

export default function BottomControls({ undo, redo, canUndo = true, canRedo = true }) {
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  const viewport = useViewport();
  const [showHelp, setShowHelp] = useState(false);

  return (
    <div style={bottomContainerStyle}>
      <div style={{position: 'relative'}}>
         {showHelp && <ShortcutsPopover onClose={() => setShowHelp(false)} />}
         <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
            <div style={zoomCapsuleStyle}>
              <ZoomBtn onClick={() => zoomOut({ duration: 300 })}><Minus size={14} /></ZoomBtn>
              <div style={{ width: '1px', height: '16px', background: 'var(--border-subtle)', margin: '0 2px' }} />
              <div style={{ width: '50px', textAlign: 'center', fontSize: '12px', color: 'var(--text-body)', fontFamily: 'monospace', userSelect: 'none' }}>
                {(viewport.zoom * 100).toFixed(0)}%
              </div>
              <div style={{ width: '1px', height: '16px', background: 'var(--border-subtle)', margin: '0 2px' }} />
              <ZoomBtn onClick={() => zoomIn({ duration: 300 })}><Plus size={14} /></ZoomBtn>
            </div>
            <HelpBtn onClick={() => setShowHelp(!showHelp)} />
            <ThemeBtn />
            <SnapBtn />
            {/* 快捷操作：适应图板 / 重做 / 撤销（圆形，与上方按钮同款） */}
            <ActionBtn onClick={() => fitView({ duration: 500, padding: 0.1 })} title="适应图板 (Z)">
              <LayoutGrid size={18} strokeWidth={1.5} />
            </ActionBtn>
            <ActionBtn onClick={redo} disabled={!canRedo} title="重做 (Ctrl+Shift+Z)">
              <CornerUpRight size={18} strokeWidth={2} />
            </ActionBtn>
            <ActionBtn onClick={undo} disabled={!canUndo} title="撤销 (Ctrl+Z)">
              <CornerUpLeft size={18} strokeWidth={2} />
            </ActionBtn>
         </div>
      </div>
    </div>
  );
}